import type { LanHello } from './types';

export interface LanNotification {
  type: 'notification';
  name: string;
  notification_id?: string;
  data?: any;
}

interface LanResponse {
  type: 'response';
  id: string;
  ok: boolean;
  result?: any;
  error?: { code?: string; message?: string };
}

interface PendingCall {
  resolve: (value: any) => void;
  reject: (reason: Error) => void;
  timer: number;
}

interface OpenAttempt {
  socket: WebSocket;
  ready: Promise<void>;
}

const MAX_CONSECUTIVE_FAILURES = 10;
const RECONNECT_FAILED_REASON = "Couldn't reach the controller.";

export type LanConnectionState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'failed';

function stableAppDeviceId(): string {
  const storageKey = 'nuance-venue:web-device-id';
  const stored = localStorage.getItem(storageKey);
  if (stored) return stored;
  const value = `web_${crypto.randomUUID()}`;
  localStorage.setItem(storageKey, value);
  return value;
}

export function normalizeLanAddress(value: string, pageProtocol = location.protocol): { socketUrl: string; key: string } {
  const raw = value.trim().replace(/\/$/, '');
  if (!raw) throw new Error('Enter the controller address.');
  let parsed: URL;
  if (/^wss?:\/\//i.test(raw)) {
    parsed = new URL(raw);
  } else if (/^https?:\/\//i.test(raw)) {
    const url = new URL(raw);
    parsed = new URL(`${url.protocol === 'https:' ? 'wss:' : 'ws:'}//${url.host}${url.pathname}`);
  } else {
    const scheme = pageProtocol === 'https:' ? 'wss:' : 'ws:';
    parsed = new URL(`${scheme}//${raw}`);
  }
  if (!parsed.port) parsed.port = '8765';
  if (!parsed.pathname || parsed.pathname === '/') parsed.pathname = '/ws';
  if (parsed.protocol === 'ws:' && pageProtocol === 'https:') {
    throw new Error('Open the controller’s local console first; secure cloud pages cannot open an insecure LAN socket.');
  }
  return { socketUrl: parsed.toString(), key: parsed.host };
}

export class LanClient {
  readonly appDeviceId = stableAppDeviceId();
  private socket: WebSocket | null = null;
  private pending = new Map<string, PendingCall>();
  private token = '';
  private addressKey = '';
  private intentionalClose = false;
  private reconnectTimers = new Set<number>();
  private reconnectAttempt = 0;
  private socketCleanups = new WeakMap<WebSocket, (reason?: string) => void>();

  onNotification?: (notification: LanNotification) => void;
  onState?: (state: LanConnectionState, reason?: string) => void;
  onHello?: (hello: LanHello) => void;

  get connected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  get key(): string {
    return this.addressKey;
  }

  async connect(address: string): Promise<LanHello> {
    const normalized = normalizeLanAddress(address);
    this.disconnect('switching controllers');
    this.intentionalClose = false;
    this.addressKey = normalized.key;
    this.token = localStorage.getItem(this.tokenKey()) ?? '';
    this.onState?.('connecting');
    let attempt: OpenAttempt | undefined;
    try {
      attempt = this.open(normalized.socketUrl);
      await attempt.ready;
      const hello = await this.call<LanHello>('hello');
      if (attempt.socket !== this.socket) throw new Error('The controller connection changed.');
      this.reconnectAttempt = 0;
      this.onHello?.(hello);
      this.onState?.('connected');
      return hello;
    } catch (error) {
      if (attempt) this.closed(attempt.socket, error instanceof Error ? error.message : String(error), normalized.socketUrl);
      else this.onState?.('failed', RECONNECT_FAILED_REASON);
      throw error;
    }
  }

  private open(socketUrl: string): OpenAttempt {
    const socket = new WebSocket(socketUrl);
    this.socket = socket;
    const ready = new Promise<void>((resolve, reject) => {
      let didOpen = false;
      const fail = () => reject(new Error('Could not open the controller connection.'));
      const opened = () => {
        didOpen = true;
        socket.removeEventListener('error', fail);
        resolve();
      };
      const message = (event: MessageEvent) => this.route(String(event.data));
      const close = (event: CloseEvent) => {
        const reason = event.reason || `connection closed (${event.code})`;
        if (!didOpen) reject(new Error(reason));
        this.closed(socket, reason, socketUrl);
      };
      this.socketCleanups.set(socket, (reason = 'Controller connection closed.') => {
        socket.removeEventListener('open', opened);
        socket.removeEventListener('error', fail);
        socket.removeEventListener('message', message);
        socket.removeEventListener('close', close);
        reject(new Error(reason));
      });
      socket.addEventListener('open', opened, { once: true });
      socket.addEventListener('error', fail, { once: true });
      socket.addEventListener('message', message);
      socket.addEventListener('close', close);
    });
    return { socket, ready };
  }

  private route(raw: string): void {
    let frame: LanResponse | LanNotification;
    try {
      frame = JSON.parse(raw) as LanResponse | LanNotification;
    } catch {
      return;
    }
    if (frame.type === 'notification') {
      this.onNotification?.(frame);
      return;
    }
    const pending = this.pending.get(frame.id);
    if (!pending) return;
    this.pending.delete(frame.id);
    globalThis.clearTimeout(pending.timer);
    if (frame.ok) pending.resolve(frame.result);
    else pending.reject(new Error(`${frame.error?.code ?? 'controller-error'}: ${frame.error?.message ?? 'Command refused.'}`));
  }

  private closed(socket: WebSocket, reason: string, socketUrl: string): void {
    // A failed WebSocket normally emits `error` and then `close`. The rejected open/hello attempt and
    // the close event therefore race through this method; only the current socket owns teardown and one
    // reconnect timer. A late event from an older generation can never resurrect the client.
    if (socket !== this.socket) return;
    if (this.reconnectTimers.size > 0) return;
    this.detachSocket(socket, reason);
    try {
      socket.close();
    } catch {
      // The browser may already have closed it. Teardown and the reconnect budget still continue.
    }
    this.socket = null;
    for (const request of this.pending.values()) {
      globalThis.clearTimeout(request.timer);
      request.reject(new Error(`Controller disconnected: ${reason}`));
    }
    this.pending.clear();
    if (this.intentionalClose) {
      this.onState?.('idle', reason);
      return;
    }
    this.scheduleReconnect(reason, socketUrl);
  }

  private scheduleReconnect(reason: string, socketUrl: string): void {
    if (this.intentionalClose || this.reconnectTimers.size > 0) return;
    this.reconnectAttempt += 1;
    if (this.reconnectAttempt >= MAX_CONSECUTIVE_FAILURES) {
      this.onState?.('failed', RECONNECT_FAILED_REASON);
      return;
    }
    this.onState?.('reconnecting', reason);
    const wait = Math.min(12_000, 600 * 2 ** (this.reconnectAttempt - 1));
    const timer = globalThis.setTimeout(async () => {
      this.reconnectTimers.delete(timer);
      let attempt: OpenAttempt | undefined;
      try {
        attempt = this.open(socketUrl);
        await attempt.ready;
        const hello = await this.call<LanHello>('hello');
        if (attempt.socket !== this.socket) return;
        this.reconnectAttempt = 0;
        this.onHello?.(hello);
        this.onState?.('connected');
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        if (attempt) this.closed(attempt.socket, message, socketUrl);
        else this.scheduleReconnect(message, socketUrl);
      }
    }, wait) as unknown as number;
    this.reconnectTimers.add(timer);
  }

  async call<T = any>(name: string, params: Record<string, unknown> = {}, timeoutMs = 12_000): Promise<T> {
    if (!this.connected || !this.socket) throw new Error('Connect to the controller on LAN first.');
    const id = crypto.randomUUID();
    const envelope = {
      type: 'command',
      id,
      deviceId: this.appDeviceId,
      lanToken: this.token,
      name,
      params,
    };
    return new Promise<T>((resolve, reject) => {
      const timer = globalThis.setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`${name} timed out.`));
      }, timeoutMs) as unknown as number;
      this.pending.set(id, { resolve, reject, timer });
      try {
        this.socket?.send(JSON.stringify(envelope));
      } catch (error) {
        globalThis.clearTimeout(timer);
        this.pending.delete(id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  async pair(pin: string): Promise<{ lanToken: string; [key: string]: any }> {
    const result = await this.call<{ lanToken: string; [key: string]: any }>('pair', { pin: pin.trim() });
    this.token = result.lanToken;
    localStorage.setItem(this.tokenKey(), result.lanToken);
    return result;
  }

  adoptPairing(address: string, lanToken: string): void {
    const normalized = normalizeLanAddress(address, 'http:');
    this.addressKey = normalized.key;
    this.token = lanToken;
    localStorage.setItem(this.tokenKey(), lanToken);
  }

  hasStoredPairing(address: string): boolean {
    try {
      const normalized = normalizeLanAddress(address);
      return Boolean(localStorage.getItem(`nuance-venue:lan-token:${normalized.key}`));
    } catch {
      return false;
    }
  }

  disconnect(reason = 'closed by operator'): void {
    this.intentionalClose = true;
    for (const timer of this.reconnectTimers) globalThis.clearTimeout(timer);
    this.reconnectTimers.clear();
    const socket = this.socket;
    if (socket) {
      this.detachSocket(socket, reason);
      try {
        socket.close(1000, reason.slice(0, 120));
      } catch {
        // A socket may already be closed while its event is queued.
      }
    }
    this.socket = null;
    this.reconnectAttempt = 0;
    for (const request of this.pending.values()) {
      globalThis.clearTimeout(request.timer);
      request.reject(new Error(reason));
    }
    this.pending.clear();
    this.onState?.('idle', reason);
  }

  private detachSocket(socket: WebSocket, reason?: string): void {
    this.socketCleanups.get(socket)?.(reason);
    this.socketCleanups.delete(socket);
  }

  private tokenKey(): string {
    return `nuance-venue:lan-token:${this.addressKey}`;
  }
}
